import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { BaseButtonLink, BaseButtonOutline, BaseLoaderSpinner, BaseStatus } from 'bxcommon';
import { warningToast } from 'bxcommon/store/slices/toaster.slice';
import AppConfig from 'bxcommon/providers/AppConfig';
import { STATUS } from 'bxcommon/constants/status.constant';
import { FormWrapper } from 'bxcommon/components/FormWrapper/FormWrapper';
import { useRequestList } from 'bxcommon/context/RequestList.context';

import { SECURITY_TYPE } from '../../constants';
import { GeneralSummary } from '../Summary/GeneralSummary';
import { ProspectusSummary } from '../Summary/ProspectusSummary';
import { InvoicingSummary } from '../Summary/InvoicingSummary';
import { ProspectusHeader } from '../ProspectusHeader/ProspectusHeader';

import './ProspectusDetails.scss';

export const ProspectusDetails = (props) => {
  const { t } = useTranslation();
  const { t: common } = useTranslation('common');

  const navigate = useNavigate();
  const { detailsId } = useParams();

  const dispatch = useDispatch();
  const { list, isLoading } = useRequestList();
  const listEntry = list.find((entry) => entry.case_id === detailsId);
  const resources = useSelector((state) => state.resources);

  const { status, case_id, security_type } = listEntry || {};
  const isEquity = security_type === 1;

  const goToEdit = () => navigate(`/request/${SECURITY_TYPE[security_type]}/${case_id}`);
  const goToAddendum = () => navigate(`/addendum/${case_id}`);
  const goToFinalTerms = () => navigate(`/final-terms/${case_id}`);

  const isApproved = status === STATUS.APPROVED;

  useEffect(() => {
    if (detailsId && listEntry) {
      dispatch({ type: 'request/updateRequestField', payload: listEntry });
    }
  }, [detailsId, dispatch, listEntry]);

  if (isLoading) {
    return <BaseLoaderSpinner large />;
  }

  if (!status) {
    warningToast(common('errors.prospectusMessage'), common('errors.prospectusTitle'));
    navigate('/list');
    return <div />;
  } else {
    return (
      <ProspectusHeader>
        <FormWrapper>
          <div className="container prospectus-details">
            <div className="row prospectus-details__wrapper">
              <div className="col-2">
                <BaseButtonLink
                  iconLeft
                  icon="arrow-left"
                  onClick={() => navigate('/list')}
                  className="prospectus-details__go-back"
                >
                  {common('goBack')}
                </BaseButtonLink>
                <BaseStatus showDetails {...listEntry} />
                {isApproved && (
                  <div className="prospectus-details__additional-files">
                    <BaseButtonOutline
                      onClick={goToAddendum}
                      iconRight
                      icon="pencil"
                      data-testid="details-addendum-button"
                    >
                      {t('list.buttons.addendum')}
                    </BaseButtonOutline>
                    {listEntry.issue_type === 5 && (
                      <BaseButtonOutline
                        onClick={goToFinalTerms}
                        iconRight
                        icon="pencil"
                        data-testid="details-final-terms-button"
                      >
                        {t('list.buttons.finalTerms')}
                      </BaseButtonOutline>
                    )}
                  </div>
                )}
              </div>
              <div className="col-8 prospectus-details__summary-content">
                {AppConfig.editStatuses.includes(status) && (
                  <BaseButtonOutline
                    className="prospectus-details__controls"
                    onClick={goToEdit}
                    iconRight
                    icon="pencil"
                    data-testid="details-edit-button"
                  >
                    {t('list.buttons.editRequest')}
                  </BaseButtonOutline>
                )}

                <GeneralSummary {...props} isEquity={isEquity} request={listEntry} resources={resources} />
                <ProspectusSummary {...props} isEquity={isEquity} request={listEntry} resources={resources} />
                <InvoicingSummary {...props} isEquity={isEquity} request={listEntry} resources={resources} />
              </div>
            </div>
          </div>
        </FormWrapper>
      </ProspectusHeader>
    );
  }
};

export default ProspectusDetails;
